/**
 * Admin app i18n bootstrap (namespace `admin`).
 * Mirrors kiosk bootstrap: bundled JSON resources, `cs` fallback, locale persistence.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import csAdmin from './locales/cs/admin.json';
import enAdmin from './locales/en/admin.json';
import skAdmin from './locales/sk/admin.json';
/** Pseudo is src-bundled at runtime; keep `public/locales/pseudo/admin.json` in dual-tree parity (see `public/locales/README.md`). */
import pseudoAdmin from './locales/pseudo/admin.json';
import {
  readStoredAdminLocale,
  writeStoredAdminLocale,
  type AdminSupportedLocale,
} from './adminLocaleStorage.js';
import { registerAdminLocaleHotReload } from './i18n.viteHot.js';
import { applyTp02CzechLongLabelFixtureBridge } from './tp02CzechLongLabelFixtureBridge.js';

function normalizeAdminLocale(lng: string): AdminSupportedLocale {
  if (lng.startsWith('en')) {
    return 'en';
  }
  if (lng.startsWith('sk')) {
    return 'sk';
  }
  return 'cs';
}

const storedLocale = readStoredAdminLocale();
const urlLng =
  typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('lng')
    : null;
/** Locale priority: ?lng=pseudo|cs|en|sk → stored → default cs. */
const initialLng: AdminSupportedLocale | 'pseudo' = (() => {
  if (urlLng === 'pseudo') {
    return 'pseudo';
  }
  if (urlLng === 'cs' || urlLng === 'en' || urlLng === 'sk') {
    return urlLng;
  }
  return storedLocale ?? 'cs';
})();

void i18n.use(initReactI18next).init({
  lng: initialLng,
  fallbackLng: 'cs',
  supportedLngs: ['cs', 'en', 'sk', 'pseudo'],
  defaultNS: 'admin',
  ns: ['admin'],
  resources: {
    cs: { admin: csAdmin },
    en: { admin: enAdmin },
    sk: { admin: skAdmin },
    pseudo: { admin: pseudoAdmin },
  },
  interpolation: { escapeValue: false },
  returnNull: false,
});

if (typeof document !== 'undefined') {
  document.documentElement.lang = (() => {
    if (initialLng === 'pseudo') {
      return 'pseudo';
    }
    return initialLng;
  })();
}

i18n.on('languageChanged', (lng) => {
  if (lng === 'pseudo') {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = 'pseudo';
    }
    return;
  }
  const normalized = normalizeAdminLocale(lng);
  writeStoredAdminLocale(normalized);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = normalized;
  }
});

registerAdminLocaleHotReload(i18n);

/** Playwright TP02 Czech long-label fixture only — no-op without window fixture. */
applyTp02CzechLongLabelFixtureBridge(i18n);

export default i18n;
